import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  useStoryboards,
  useUploadStoryboard,
  useSelectStoryboard,
  useDeleteStoryboard,
} from '../../../features/scenarios/hooks';
import { useStoryboardGeneration } from './hooks/useStoryboardGeneration';
import type { Storyboard } from '../../../features/scenarios/types';

// useStoryboards の data が undefined の間、毎レンダー新しい [] が生成されると
// 下流の effect 依存が毎回変化してしまうので、安定した空配列を共有する。
const EMPTY_STORYBOARDS: Storyboard[] = [];
// useImageNaturalSizes に「プリロード不要」を伝える際の安定した空配列
// (Seedance 以外のモデルではプリロードしない)。
const EMPTY_IMAGE_URLS: string[] = [];
import { downloadFile } from '../../materials/shared/downloadUtils';
import { useToast } from '../../../hooks/useToast';
import { MOBILE_BREAKPOINT } from '../../../hooks/useIsMobile';
import { StoryboardCompactContext } from './storyboardCompactContext';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import type { CreditRatesResponse } from '../../../features/organizations/types';
import { HelpTooltip } from '../../common/HelpTooltip';
import { Tooltip } from '../../common/Tooltip';
import HoverPillButton from '../../common/HoverPillButton';
import type { DroppedMaterial, Scenario } from './types';
import { useBrandLogos } from '../hooks/useBrandLogos';
import { renderAndSaveBrandSheet, backfillMissingLogoSheets } from '../../../features/brands/logoSheet';
import { useQueryClient } from '@tanstack/react-query';
import { useGenerationState } from '../state/useGenerationState';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import { isStoryboardTextDrivenModel, isStoryboardIndividualRefsModel, stripUrlQuery } from './utils';
import { brandConfig } from '../../../config/brands';
import { SEEDANCE_MIN_REFERENCE_IMAGE_SHORT_SIDE } from './constants';
import styles from './ScenarioGenerator.module.css';
import '../../../styles/generation-common.css';

interface StoryboardCandidatesProps {
  scenario: Scenario;
  // ストーリーボードモードの動画生成ボックス（シナリオ全体で 1 本）。右列の「シーンを追加」
  // の下 (beforeVideoSection があればその後) に常時描画する。絵コンテ未選択時は disabledReason 文字列を返してボタンを
  // 非活性化＋カスタムツールチップ表示にする。
  videoBox?: (disabledReason: string | null) => ReactNode;
  // 生成ボタンのバッジに表示するクレジット消費量の算出に使う。
  creditRatesData?: CreditRatesResponse;
  // 右カラムに並べるシーン一覧（WorkPanel から渡す）。
  children?: ReactNode;
  // 動画カードの前後に並べるセクション。表示順は WorkPanel で決める。
  beforeVideoSection?: ReactNode;
  afterVideoSection?: ReactNode;
  // シナリオ設定の性別/年代フィルタ。俳優未選択 (casting_model_ids 未設定) 時、
  // 絵コンテプロンプトに「主な登場人物: 男性 / 30歳前後」の指示を出すため backend に
  // 流す。'none' / null は未指定とみなし送信しない。俳優選択時は backend 側で
  // prompt_models のデータが優先されるため、送っても害はない。
  castingGenderFilter?: string;
  castingAgeFilter?: number | null;
  // 選択素材をマス目1枚にまとめてローカルDLするハンドラ（ScenarioGenerator 由来）。
  // 暫定の素材グリッドDLアイコンで使う。選択素材そのものは useGenerationState から読む。
  handleDownloadAssetTile?: () => void;
  isDownloadingAssetTile?: boolean;
  // 「商品画像を再生成」: 選択中の素材から統合タイル画像を合成し直す (DLアイコンの右隣)
  handleRegenerateProductTile?: () => void;
  isRegeneratingProductTile?: boolean;
}

// 絵コンテ生成モデルは Gemini 標準 (nano-banana 系) に統一 (2026-08-14)。線画
// フォーマットへの全面移行に伴い、線画を得意とする Gemini を全動画モデルで使う
// (GPT Image 2 より高速・低ポイント・1K 固定)。lite (スピード) はシーン番号ラベルの
// 文字が潰れて読めないため標準を使う。2026-10-08: 標準の中身を Nano Banana 2.1 に切替
// (backend storyboard_generator.DEFAULT_STORYBOARD_MODEL と同期)。
const STORYBOARD_MODEL = 'gemini-nano-banana-2.1';

// 各コマに描かせるフレーム数の指示モード。
//   'first_and_last': スタート/エンドの 2 フレーム（既定・従来動作）
//   'first_only':     スタートフレームのみ
type StoryboardFrameMode = 'first_and_last' | 'first_only';
const STORYBOARD_FRAME_MODE_OPTIONS: { value: StoryboardFrameMode; label: string }[] = [
  { value: 'first_only', label: '最初' },
  { value: 'first_and_last', label: '最初+最後' },
];

// 枚数・フレームの最後の選択を localStorage に保存して次回起動時に復元する。
// 値はシナリオ横断で 1 セット（ユーザーの好みなのでシナリオ別にする必要なし）。
// (解像度は Gemini 統一で 1K 固定になったため選択肢と保存キーを廃止。
//  旧キー 'storyboard:image_size' は残っていても参照しない)
const STORAGE_KEY_COUNT = 'storyboard:count';
const STORAGE_KEY_FRAME_MODE = 'storyboard:frame_mode';
// 添付オプション (include_logo / include_product_image) の localStorage 管理は
// useGenerationState に移譲済み。StoryboardCandidates と useVideoHandlers の両方で
// 同じ state を参照する必要があるため。

function readStoredCount(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_COUNT);
    const n = raw == null ? NaN : Number(raw);
    if (Number.isInteger(n) && n >= 1 && n <= 5) return n;
  } catch {
    // localStorage が使えない環境では既定値にフォールバック
  }
  return 1;
}

function readStoredFrameMode(): StoryboardFrameMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FRAME_MODE);
    if (raw === 'first_only' || raw === 'first_and_last') return raw;
  } catch {
    // 同上
  }
  return 'first_only';
}


// 添付トグル右横の小さなダウンロードアイコン。動画エキスパンドと同じ download_bottom.svg
// を流用。Gemini Omni の API 公開までの暫定対応なので、控えめな見た目にしている。
function InlineDownloadButton({ onClick, busy, title }: { onClick: () => void; busy?: boolean; title: string }) {
  return (
    <button
      type="button"
      className={styles.storyboardInlineDownloadBtn}
      onClick={onClick}
      disabled={busy}
      title={title}
      aria-label={title}
    >
      {busy ? (
        <span className={styles.spinnerSmall} style={{ width: '13px', height: '13px' }} />
      ) : (
        <img src="/download_bottom.svg" alt="" className={styles.storyboardInlineDownloadIcon} />
      )}
    </button>
  );
}


// 画像 URL 一覧をバックグラウンドでプリロードし、naturalWidth/naturalHeight を取得する
// フック。DB に寸法を持たない画像 (ブランドロゴ等) の実サイズをブラウザ側で調べ、
// Seedance の参照画像下限 (短辺 px) 判定に使う。
//
// - URL ごとに一度だけロードする (既にロード試行済みの URL は再ロードしない)
// - ロード失敗 / 未ロードの URL は Map に載らない。呼び出し側は「寸法不明」を
//   「制限に抵触しない」として扱うこと (誤って除外扱いにしない方針は
//   assetMediaUtils.ts の isBelowMinReferenceSize と同じ。実際の送信除外は
//   バックエンドが安全網として行う)
// - アンマウント/urls 変化時は onload/onerror を無効化してクリーンアップする
function useImageNaturalSizes(urls: string[]): Map<string, { width: number; height: number }> {
  const [sizes, setSizes] = useState<Map<string, { width: number; height: number }>>(new Map());
  const attemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const images: HTMLImageElement[] = [];
    for (const url of urls) {
      if (!url || attemptedRef.current.has(url)) continue;
      attemptedRef.current.add(url);
      const img = new Image();
      img.onload = () => {
        setSizes((prev) => {
          const next = new Map(prev);
          next.set(url, { width: img.naturalWidth, height: img.naturalHeight });
          return next;
        });
      };
      img.onerror = () => {
        // 失敗時は試行済みマークを外し、後で同じ URL が再度渡されたら再トライできるようにする
        attemptedRef.current.delete(url);
      };
      img.src = url;
      images.push(img);
    }
    return () => {
      for (const img of images) {
        img.onload = null;
        img.onerror = null;
      }
    };
  }, [urls]);

  return sizes;
}

// 個別添付モデル (Seedance 2.5 等) 用: 添付トグル右横の小さな目アイコン。ホバーで
// 実際に添付される画像 (個別ロゴ / 選択素材) をサムネイル一覧でプレビューする。
// シート/タイル合成をしないためダウンロード・再生成の対象物が無く、DL ボタンの代替。
function InlineAttachPreviewButton({
  images,
  title,
  dimmedUrls,
  dimmedNote,
}: {
  images: string[];
  title: string;
  // 実際には送信されない画像 (Seedance の参照画像下限未満) の URL 集合。
  // 未指定 (undefined) なら従来どおり全画像を通常表示する。
  dimmedUrls?: Set<string>;
  // dimmedUrls に該当する画像が1枚以上あるときだけプレビュー内に出す注記。
  dimmedNote?: string;
}) {
  if (images.length === 0) return null;
  const hasDimmed = !!dimmedUrls && images.some((url) => dimmedUrls.has(url));
  return (
    <Tooltip
      content={
        <div className={styles.storyboardAttachPreview}>
          <div className={styles.storyboardAttachPreviewTitle}>{title}</div>
          {hasDimmed && dimmedNote && (
            <div className={styles.storyboardAttachPreviewNote}>{dimmedNote}</div>
          )}
          <div className={styles.storyboardAttachPreviewGrid}>
            {images.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                className={`${styles.storyboardAttachPreviewThumb} ${dimmedUrls?.has(url) ? styles.storyboardAttachPreviewThumbDimmed : ''}`}
              />
            ))}
          </div>
        </div>
      }
    >
      <button
        type="button"
        className={styles.storyboardInlineDownloadBtn}
        aria-label={title}
      >
        <img src="/view_square.svg" alt="" className={styles.storyboardInlineDownloadIcon} />
        {/* 除外対象が1枚でもあるとき、目アイコン右上に小さな赤ポッチで気付けるようにする */}
        {hasDimmed && <span className={styles.storyboardAttachPreviewBadge} aria-hidden />}
      </button>
    </Tooltip>
  );
}


/**
 * ストーリーボードモード: シナリオ全体の絵コンテ候補（1〜5枚）を生成・表示し、
 * 動画化に使う 1 枚を選択する。
 * - 左列: ストーリーボードのカード（タイトル・枚数/解像度・生成ボタンを縦並び）と
 *   その下に絵コンテ候補/プレースホルダを縦並び。未生成でも枚数ぶん並べる
 * - 右列: シーン一覧（children）+ beforeVideoSection + 動画カード（videoBox）+ afterVideoSection
 */
export function StoryboardCandidates({ scenario, videoBox, creditRatesData, children, beforeVideoSection, afterVideoSection, castingGenderFilter, castingAgeFilter, handleDownloadAssetTile, isDownloadingAssetTile = false, handleRegenerateProductTile, isRegeneratingProductTile = false }: StoryboardCandidatesProps) {
  const [count, setCount] = useState<number>(readStoredCount);
  const [frameMode, setFrameMode] = useState<StoryboardFrameMode>(readStoredFrameMode);

  // 最後の選択を localStorage に保存（次回も同じ設定で起動できるように）
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_COUNT, String(count)); } catch { /* noop */ }
  }, [count]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_FRAME_MODE, frameMode); } catch { /* noop */ }
  }, [frameMode]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // 左列(枚数等の設定 + 候補一覧)の折り畳み状態。広い画面でシーン側を広く使いたいときに畳む。
  // 初期表示の推定: メイン領域が狭い (モバイル幅 or 素材パネルが既に開いている) 場合は畳んで
  // 始める。マウント後は下の ResizeObserver が実幅で確定させるため、これはちらつき回避の初期値。
  const [isColumnCollapsed, setIsColumnCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const panelOpen = useMaterialsPanelStore.getState().isOpen;
    return panelOpen || window.innerWidth <= MOBILE_BREAKPOINT;
  });
  // studio 領域が狭いとき (PC でも素材パネル展開などで 2 カラムの実幅が足りない) は、
  // モバイルと同じく列を縦積みにしてストーリーボード生成導線をシーンの上に出す。
  // viewport ベースのメディアクエリでは拾えないので、下の ResizeObserver の実幅レジームで駆動する。
  const [isNarrowLayout, setIsNarrowLayout] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const panelOpen = useMaterialsPanelStore.getState().isOpen;
    return panelOpen || window.innerWidth <= MOBILE_BREAKPOINT;
  });
  // 2カラムレイアウト(.storyboardLayout)の実幅を測る参照と、現在の幅レジーム。
  // レジームは畳み/開きのヒステリシス判定に使い、初期値は上の初期表示推定に合わせる。
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const widthRegimeRef = useRef<'wide' | 'narrow'>(isColumnCollapsed ? 'narrow' : 'wide');
  // 「詳細設定」（解像度・フレーム）の開閉。既定は閉じる。
  const [showAdvanced, setShowAdvanced] = useState(false);
  // ロゴ画像 / メイン画像の添付チェック。useGenerationState で localStorage 永続化済み。
  // ロゴは静止画 + 動画両方の参照画像に、メイン画像は動画のみに使う (送信時に分岐)。
  // useVideoHandlers でも同じ state を読むため、グローバル state に持つ。
  // ロゴは登録ロゴを1枚にまとめた「ロゴシート」をバックエンドが添付するため、
  // ここでは ON/OFF のみ。個別ロゴの選択は行わない。
  // avoidFaces はシナリオ設定画面 (SetupPanel) の「顔を描かない」と同一のグローバル state。
  // 設定し忘れに編集画面側で気づいて切り替えられるよう、ここにもチェックを露出する
  // (どちらで変えても双方向に同期する)。
  const {
    includeStoryboardLogo: includeLogo,
    setIncludeStoryboardLogo: setIncludeLogo,
    includeStoryboardProductImage: includeProductImage,
    setIncludeStoryboardProductImage: setIncludeProductImage,
    selectedAssets,
    avoidFaces,
    setAvoidFaces,
    illustrationStoryboard,
    setIllustrationStoryboard,
    visualStyle,
    shootingMethod,
    visualStyleIsStylized,
    videoSettings,
    facePrivacyFlagged,
  } = useGenerationState();

  // Seedance 実在人物フィルタの原因素材 (このシナリオの動画生成失敗時のみ)。
  // 該当するチェックボックス・絵コンテカードを赤枠で強調する。対処ボタンは
  // SceneVideoBox の原因バナー側。
  const facePrivacyItems = useMemo(
    () => (facePrivacyFlagged && facePrivacyFlagged.scenarioId === scenario.id
      ? facePrivacyFlagged.items
      : []),
    [facePrivacyFlagged, scenario.id],
  );
  const facePrivacyKinds = useMemo(
    () => new Set(facePrivacyItems.map((it) => it.kind)),
    [facePrivacyItems],
  );
  const facePrivacyLogoFlagged = facePrivacyKinds.has('logo');
  const facePrivacyProductFlagged = facePrivacyKinds.has('product_image') || facePrivacyKinds.has('product_tile');
  // 絵コンテ画像の原因 URL (クエリ除去済み)。候補カードと突き合わせて赤枠を付ける
  const facePrivacyStoryboardUrls = useMemo(
    () => new Set(
      facePrivacyItems
        .filter((it) => it.kind === 'storyboard' && it.url)
        .map((it) => stripUrlQuery(it.url)),
    ),
    [facePrivacyItems],
  );

  // ロゴ / 商品画像の添付チェックが原因のときは、赤枠が見えるよう詳細設定を自動で開く
  useEffect(() => {
    if (facePrivacyLogoFlagged || facePrivacyProductFlagged) {
      setShowAdvanced(true);
    }
  }, [facePrivacyLogoFlagged, facePrivacyProductFlagged]);

  // 非写実系スタイルでは絵コンテをスタイル指示優先で描くため、
  // 顔を描かない/イラスト絵コンテオプションは送らない。
  // stylized 判定は DB の visual_family 由来 (OEM 独自スタイル対応)。
  const isStylized = visualStyleIsStylized;

  // テキスト駆動モデル (DB フラグ。例: Seedance 2.5) は映像スタイルを問わず絵コンテが
  // テキスト駆動の目視確認用 (動画生成の参照に使われない) のため、安価・高速な
  // Gemini 標準 (1K 固定) で絵コンテを生成する。
  const { data: allVideoModels } = useGenerationModels('video');
  // テキスト駆動モデル (Seedance 2.5 等) では絵コンテが動画生成に使われず目視確認専用。
  // フレーム選択の非表示・first_only 強制と解像度ヘルプの文言に使う
  // (生成モデル自体は全動画モデルで Gemini 標準 = STORYBOARD_MODEL に統一済み)。
  const textDrivenStoryboard = isStoryboardTextDrivenModel(videoSettings.videoModel, allVideoModels);
  const storyboardModel = STORYBOARD_MODEL;

  // 個別添付モデル (Seedance 2.5 等): 動画生成時、ロゴ/選択素材をシート/タイルに合成せず
  // 1 枚ずつ添付する (useVideoHandlers + backend references.py)。この場合シート/タイルの
  // ダウンロード・再生成ボタンは対象物が無いため出さず、代わりに添付される画像を
  // ホバーで確認できる目アイコンを出す。
  const individualRefsMode = isStoryboardIndividualRefsModel(videoSettings.videoModel, allVideoModels);

  // Seedance (BytePlus) は参照画像の短辺 300px 未満を拒否し、backend が送信前に
  // 自動除外する。個別添付ロゴのうちどれが除外されるか画面上分からないため、
  // Seedance のときだけ実サイズをプリロードしてプレビューに反映する
  // (Seedance 以外の個別添付モデル (Gemini Omni 等) はこの制約が無いため対象外)。
  // 判定方法は GenerationWorkspace.tsx / CastingSection.tsx の isSeedanceVideoModel と同じ。
  const isSeedanceVideoModel = (videoSettings.videoModel || '').startsWith('seedance');

  // シナリオの商品 (ASIN or manual product_id) からブランドロゴ可否を判定
  const scenarioAsins = useMemo(
    () => (scenario.asin ? [scenario.asin.toUpperCase()] : []),
    [scenario.asin],
  );
  const scenarioProductIds = useMemo(() => {
    const fromCustomInput = (scenario.custom_input as Record<string, unknown> | undefined)?.product_id;
    const pid = scenario.product_id || (typeof fromCustomInput === 'string' ? fromCustomInput : null);
    return pid ? [pid] : [];
  }, [scenario.product_id, scenario.custom_input]);
  const brandLogosQuery = useBrandLogos(scenarioAsins, { productIds: scenarioProductIds });
  const brandLogosAvailable = useMemo(() => {
    if (!brandLogosQuery.data) return false;
    return brandLogosQuery.data.some((brand) => brand.logos && brand.logos.length > 0);
  }, [brandLogosQuery.data]);
  // 「商品画像を添付する」(動画生成専用) チェックの活性条件: シナリオに商品 (ASIN or
  // manual product_id) が紐づいているか。商品メイン画像を動画の参照に足す機能なので
  // 商品紐付けの有無で判定する。
  const hasScenarioProduct = scenarioAsins.length > 0 || scenarioProductIds.length > 0;
  // ブランドロゴが使えないシナリオでも state は触らない: ユーザーの「使いたい」希望を
  // localStorage 経由で保持し続け、商品にロゴが紐づく状態に戻ったら自動的にチェックが
  // 復活する。送信時は handleGenerate 側で brandLogosAvailable を見てスキップする。
  // 拡大ボタン: 素材パネル(右パネル)をストーリーボードプレビューに転換
  const openStoryboardPreview = useMaterialsPanelStore((s) => s.openStoryboardPreview);
  const setStoryboardItems = useMaterialsPanelStore((s) => s.setStoryboardItems);
  // 素材パネルを開く（アップロードタイルのクリック / 「素材を選択」リンクから利用）。
  const openMaterialsPanel = useMaterialsPanelStore((s) => s.open);
  // 右パネルが現在ストーリーボードタブを保持しているか。
  // storyboardImageUrl が立っていればタブが表示済み、サムネクリック時に画像を差し替える。
  const rightPanelOpen = useMaterialsPanelStore((s) => s.isOpen);
  const rightPanelStoryboardUrl = useMaterialsPanelStore((s) => s.storyboardImageUrl);
  const isStoryboardPanelOpen = rightPanelOpen && rightPanelStoryboardUrl != null;
  // パネルに現在反映済みのシナリオ id。シナリオ切替時の自動差し替え(下の useEffect)が
  // 一度きりで済むようにし、同一シナリオ内でのユーザー操作(拡大・選択)を上書きしないための番兵。
  const panelSyncedScenarioIdRef = useRef<string | null>(null);

  // 左列を畳む/開くは「メイン領域(2カラムの実幅)」を ResizeObserver で実測して決める。
  // 素材パネルのせり出し・幅ドラッグ・ウィンドウリサイズ・モバイル化のいずれでも実幅が
  // 変わるので、これ一つで「狭くなったら畳む / 広くなったら開く」を賄える。
  // ヒステリシス付き (畳む閾値 < 開く閾値): 閾値付近でパネル幅を頻繁に動かしてもバタつかず、
  // 同じレジーム内ではユーザーの手動トグルが尊重される (境界をまたいだ時だけ反映)。
  useEffect(() => {
    const el = layoutRef.current;
    if (!el) return;
    const COLLAPSE_BELOW = 520; // これ未満に縮んだら畳む
    const EXPAND_ABOVE = 640;   // これ以上に広がったら開く
    const apply = (width: number) => {
      if (!width) return; // display:none 等で 0 の時は判定しない
      if (widthRegimeRef.current === 'wide' && width < COLLAPSE_BELOW) {
        widthRegimeRef.current = 'narrow';
        setIsColumnCollapsed(true);
        setIsNarrowLayout(true);
      } else if (widthRegimeRef.current === 'narrow' && width >= EXPAND_ABOVE) {
        widthRegimeRef.current = 'wide';
        setIsColumnCollapsed(false);
        setIsNarrowLayout(false);
      }
    };
    apply(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toast = useToast();
  const { data: storyboards = EMPTY_STORYBOARDS } = useStoryboards(scenario.id);

  // 右パネルの「ストーリーボード」タブの送り/戻すボタンが使う候補一覧を共有する。
  // letter_index で昇順並びは StoryboardCandidates のレンダリングと同じ規約。
  useEffect(() => {
    const items = storyboards
      .map((sb, i) => {
        const url = sb.image_url || sb.thumbnail_url;
        if (!url) return null;
        const letterIdx = typeof sb.letter_index === 'number' ? sb.letter_index : i;
        // id は scenario_storyboards 行 ID、materialId は素材 ID (別物)。
        // 素材詳細モーダルを開くには materialId を使う。
        return { id: sb.id, url, label: String.fromCharCode(65 + letterIdx), materialId: sb.material_id ?? null };
      })
      .filter((it): it is { id: string; url: string; label: string; materialId: string | null } => it != null);
    setStoryboardItems(items);
  }, [storyboards, setStoryboardItems]);

  // ストーリーボードパネルを開いたままシナリオ(タブ)を切り替えたら、切替先シナリオの
  // 「選択中の絵コンテ」をパネルに自動反映する。未選択ならデフォルトでA(最小 letter_index)。
  // 同一シナリオ内のユーザー操作(拡大・候補クリック)は ref を更新済みなので上書きしない。
  useEffect(() => {
    // パネルを閉じている間は番兵をリセットし、次に開く/切り替えた時に必ず再同期させる
    if (!isStoryboardPanelOpen) {
      panelSyncedScenarioIdRef.current = null;
      return;
    }
    // 既にこのシナリオの絵コンテをパネルへ反映済みなら何もしない
    if (panelSyncedScenarioIdRef.current === scenario.id) return;
    // 画像を持つ候補を letter_index 昇順に並べ、選択中→無ければ先頭(A) を採用
    const views = storyboards
      .map((sb, i) => {
        const url = sb.image_url || sb.thumbnail_url;
        if (!url) return null;
        const letterIdx = typeof sb.letter_index === 'number' ? sb.letter_index : i;
        return { sb, url, label: String.fromCharCode(65 + letterIdx), letterIdx };
      })
      .filter((v): v is NonNullable<typeof v> => v != null)
      .sort((a, b) => a.letterIdx - b.letterIdx);
    if (views.length === 0) return; // まだ読み込み中 / 候補なしなら次の更新を待つ
    const target = views.find((v) => v.sb.is_selected) ?? views[0];
    openStoryboardPreview(target.url, target.label, scenario.title);
    panelSyncedScenarioIdRef.current = scenario.id;
  }, [isStoryboardPanelOpen, scenario.id, scenario.title, storyboards, openStoryboardPreview]);
  // 次に生成されるストーリーボードの letter index (既存の最大 letter_index + 1)。
  // 生成キューの上段ラベル「{シナリオ名} - {A,B,...}」に使う。
  const nextStoryboardLetterIndex = useMemo(() => {
    if (storyboards.length === 0) return 0;
    const maxIdx = Math.max(
      ...storyboards.map((sb, i) => (typeof sb.letter_index === 'number' ? sb.letter_index : i)),
    );
    return maxIdx + 1;
  }, [storyboards]);
  // ストーリーボード生成はシナリオ単位のジョブとして起動・追跡する。生成中状態
  // (isGenerating) は activeJobs から導出されるため、別シナリオへ遷移して当
  // コンポーネントが unmount → 再マウントしても進捗が途切れない。
  const { generate: generateStoryboard, isGenerating, error: generateError } = useStoryboardGeneration(scenario.id, scenario.title, nextStoryboardLetterIndex);
  const selectMutation = useSelectStoryboard();
  const deleteMutation = useDeleteStoryboard();
  const uploadMutation = useUploadStoryboard();

  // アップロードタイル: 隠し input への参照とドラッグオーバー状態
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // 共通アップロードハンドラ。素材ドロップ / ブラウザファイル DnD いずれもこれを通す。
  // 大文字小文字どちらでも拾えるよう MIME と拡張子の両方で雑にチェック。
  const handleUploadFile = (file: File) => {
    const ext = file.name.toLowerCase().split('.').pop() || '';
    const okExt = ['png', 'jpg', 'jpeg', 'webp'].includes(ext);
    const okMime = ['image/png', 'image/jpeg', 'image/webp'].includes(file.type);
    if (!okExt || !okMime) {
      toast.error('PNG / JPEG / WebP の画像ファイルを選択してください');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('ファイルサイズは 50MB までです');
      return;
    }
    uploadMutation.mutate(
      { scenarioId: scenario.id, file },
      {
        onError: (err) => toast.error((err as Error)?.message || '画像のアップロードに失敗しました'),
      },
    );
  };

  // 素材パネルから drop された素材データ (buildDragData) を File 化してアップロードする。
  // sourceUrl を fetch して Blob → File に変換し、既存の handleUploadFile に流す。
  const handleUploadMaterial = async (materialData: DroppedMaterial) => {
    const sourceUrl = materialData?.sourceUrl;
    if (!sourceUrl) {
      toast.error('素材のURLが見つかりません');
      return;
    }
    if (materialData?.contentType && !String(materialData.contentType).startsWith('image/')) {
      toast.error('画像素材のみストーリーボードに使用できます');
      return;
    }
    try {
      const resp = await fetch(sourceUrl);
      const blob = await resp.blob();
      const mime = blob.type || materialData?.contentType || 'image/png';
      const extFromMime = mime.split('/')[1] || 'png';
      const ext = extFromMime === 'jpeg' ? 'jpg' : extFromMime;
      const file = new File([blob], `storyboard.${ext}`, { type: mime });
      handleUploadFile(file);
    } catch {
      toast.error('素材の取得に失敗しました');
    }
  };

  const hasSelection = storyboards.some((s) => s.is_selected);

  // 生成ボタンのバッジに出す消費クレジット = 1 枚あたりレート × 枚数。
  // Gemini は 1K 固定生成のため resource_type は model_code そのまま
  // (backend _resolve_image_resource_type の規約。標準 15pt / lite 10pt)。
  const perImageCredits = creditRatesData?.rates.find(
    (r) => r.service_type === 'image_generation' && r.resource_type === storyboardModel,
  )?.credits_per_unit;
  const storyboardCredits = perImageCredits != null ? perImageCredits * count : null;

  // 列に並べるスロット数:
  //  - 生成中: 既存候補はそのまま並べ、追加生成ぶん (count) のプレースホルダを後ろに足す
  //  - 通常: 枚数と既存候補数の多い方（余りはプレースホルダ）
  const slotCount = isGenerating ? storyboards.length + count : Math.max(count, storyboards.length);

  const handleGenerate = async () => {
    // シナリオ設定の性別/年代フィルタは、俳優 (casting_model_ids) が未選択でも
    // 絵コンテに登場人物のヒントを残せるよう backend に流す。
    // 'none' / null / undefined は未指定として送らない。
    const castingGender = castingGenderFilter && castingGenderFilter !== 'none' ? castingGenderFilter : undefined;
    const castingAge = typeof castingAgeFilter === 'number' ? castingAgeFilter : undefined;
    // 静止画(絵コンテ)の参照画像は「選択中の素材 (selectedAssets)」を正とする。
    // 既定で商品画像が選択されているが、ユーザーが外す/別画像を足せる。常にキーを送り
    // (空配列も含む)、backend 側で商品テーブルの自動取得を置き換える。全解除した場合は
    // 空配列を送って「参照画像なしで生成」できるようにする。
    const selectedAssetUrls = selectedAssets
      .map((a) => (a.sourceUrl ?? '').trim())
      .filter((u) => u.length > 0);
    // 素材IDを併送し、backend が素材名を解決して参照画像ラベルに使えるようにする
    // (selected_asset_urls は後方互換のため維持)
    const selectedAssetRefs = selectedAssets
      .filter((a) => (a.sourceUrl ?? '').trim().length > 0)
      .map((a) => ({ url: a.sourceUrl!.trim(), material_id: a.id }));

    // ロゴ添付時は、生成リクエストを投げる前に未生成ブランドのロゴシートを
    // サーバー保存しておく。これをしないと backend が「シート未生成」と判断して
    // 個別ロゴを1枚ずつ添付するフォールバックに落ちてしまう (順序が重要)。
    if (includeLogo && brandLogosAvailable) {
      setPreparingLogoSheet(true);
      try {
        await backfillMissingLogoSheets(brandLogosQuery.data ?? [], queryClient);
      } finally {
        setPreparingLogoSheet(false);
      }
    }

    generateStoryboard({
      count,
      model: storyboardModel,
      // テキスト駆動モデルではフレーム選択 UI を出さず、常に最初のフレームのみ
      // (localStorage に残った 'first_and_last' を送らない)
      frame_mode: textDrivenStoryboard ? 'first_only' : frameMode,
      // image_size は送らない (Gemini は backend が 1K 固定で生成する)
      avoid_faces: (!isStylized && avoidFaces) || undefined,
      illustration_storyboard: (!isStylized && illustrationStoryboard) || undefined,
      visual_style: visualStyle,
      shooting_method: shootingMethod,
      include_logo: includeLogo && brandLogosAvailable ? true : undefined,
      casting_gender: castingGender,
      casting_age: castingAge,
      selected_asset_urls: selectedAssetUrls,
      selected_assets: selectedAssetRefs,
      // 「商品画像を添付する」(includeProductImage) は動画生成側のみ。静止画には送らない。
    });
  };

  // 拡大ボタン: 素材パネル(右パネル)をストーリーボード表示に転換して絵コンテ画像を出す
  const handleExpand = (sb: Storyboard, label: string) => {
    const url = sb.image_url || sb.thumbnail_url;
    if (!url) return;
    openStoryboardPreview(url, label, scenario.title);
    panelSyncedScenarioIdRef.current = scenario.id;
  };

  // 折り畳み時の文字バッジクリック: 選択状態を切替し、サイドパネルを
  // ストーリーボード表示で開く（既に開いていれば閉じずに画像を差し替える）。
  // 折り畳み時はサムネが見えないため、クリックで常にプレビューを出す方が自然。
  const handleLetterClick = (sb: Storyboard, label: string) => {
    selectMutation.mutate({ scenarioId: scenario.id, storyboardId: sb.id });
    const url = sb.image_url || sb.thumbnail_url;
    if (url) {
      openStoryboardPreview(url, label, scenario.title);
      panelSyncedScenarioIdRef.current = scenario.id;
    }
  };

  // ダウンロードボタン: 絵コンテ画像をブラウザに保存。
  // ファイル名は {シナリオ名}_{A,B,...}.png（letter は表示中のレターバッジと一致）。
  const handleDownload = async (sb: Storyboard, letter: string) => {
    const url = sb.image_url || sb.thumbnail_url;
    if (!url || downloadingId) return;
    setDownloadingId(sb.id);
    try {
      const safeTitle =
        (scenario.title || '').replace(/[\\/:*?"<>|]/g, '').trim() || 'storyboard';
      await downloadFile(url, `${safeTitle}_${letter}.png`);
    } catch {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setDownloadingId(null);
    }
  };

  // ロゴ1枚シートの描画・バックフィルは features/brands/logoSheet.ts に集約 (自動生成
  // フローと共通化)。「ロゴ1枚シート」(生成 API に渡すのと同じ canonical なシート) は
  // Canvas 描画のためブラウザでしか作れない。未生成ブランドはバックエンドが生成時に
  // 作れず個別ロゴへフォールバックしてしまうので、生成/ダウンロード前に描画して保存する。
  const queryClient = useQueryClient();
  const [downloadingLogoSheet, setDownloadingLogoSheet] = useState(false);
  const [preparingLogoSheet, setPreparingLogoSheet] = useState(false);

  // 優先: 保存済みシートを持つブランド。無ければロゴを持つ (= シート生成可能な) ブランド。
  const logoSheetBrand = useMemo(() => {
    const brands = brandLogosQuery.data ?? [];
    return brands.find((b) => b.logo_sheet_url) ?? brands.find((b) => b.logos && b.logos.length > 0);
  }, [brandLogosQuery.data]);

  // 個別添付モデルのホバープレビュー用: 実際に添付される個別ロゴ画像の URL 一覧
  const individualLogoUrls = useMemo(() => {
    const urls: string[] = [];
    for (const brand of brandLogosQuery.data ?? []) {
      for (const bl of brand.logos ?? []) {
        const url = bl.logos?.url;
        if (url && !urls.includes(url)) urls.push(url);
      }
    }
    return urls;
  }, [brandLogosQuery.data]);

  // Seedance のときだけ個別ロゴの実サイズをプリロードする (不要なプリロードをしない)。
  const seedanceLogoPreloadUrls = individualRefsMode && isSeedanceVideoModel ? individualLogoUrls : EMPTY_IMAGE_URLS;
  const logoNaturalSizes = useImageNaturalSizes(seedanceLogoPreloadUrls);
  // 実際には送信されない (短辺 300px 未満の) ロゴ URL の集合。寸法未取得のものは
  // 誤って除外扱いにしない (実際の送信除外は backend が安全網として行う)。
  const dimmedLogoUrls = useMemo(() => {
    if (!individualRefsMode || !isSeedanceVideoModel) return undefined;
    const set = new Set<string>();
    for (const url of individualLogoUrls) {
      const size = logoNaturalSizes.get(url);
      if (size && Math.min(size.width, size.height) < SEEDANCE_MIN_REFERENCE_IMAGE_SHORT_SIDE) {
        set.add(url);
      }
    }
    return set;
  }, [individualRefsMode, isSeedanceVideoModel, individualLogoUrls, logoNaturalSizes]);

  const handleDownloadLogoSheet = async () => {
    if (downloadingLogoSheet || !logoSheetBrand) return;
    setDownloadingLogoSheet(true);
    try {
      const safeName =
        (logoSheetBrand.brand_name || 'logo').replace(/[\\/:*?"<>|]/g, '_').trim() || 'logo';
      const filename = `${safeName}_logo_sheet.png`;

      // 1) 保存済みシートがあればそのまま落とす
      if (logoSheetBrand.logo_sheet_url) {
        await downloadFile(logoSheetBrand.logo_sheet_url, filename);
        return;
      }

      // 2) 未生成: 正規シートをこの場で生成 → サーバー保存 → 保存済みシートを落とす
      const { savedUrl, blob } = await renderAndSaveBrandSheet(logoSheetBrand);
      if (savedUrl) {
        queryClient.invalidateQueries({ queryKey: ['brand-logos'] });
        await downloadFile(savedUrl, filename);
      } else if (blob) {
        // 保存できなかった場合でも、生成したシートをそのまま落とす
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else {
        toast.error('ロゴシートの生成に失敗しました');
      }
    } catch {
      toast.error('ロゴシートのダウンロードに失敗しました');
    } finally {
      setDownloadingLogoSheet(false);
    }
  };

  // 絵コンテ候補 1 枚分のカード（選択・拡大・ダウンロード・削除）。
  // letter は A,B,C... の文字でパネルタイトルにも反映される。
  // 通常時はサムネ + レターバッジのみ。hover/選択時に操作ボタンを表示する。
  const renderStoryboardCard = (sb: Storyboard, letter: string) => {
    const url = sb.thumbnail_url || sb.image_url || '';
    const expandDisabled = !(sb.image_url || sb.thumbnail_url);
    const downloadDisabled = downloadingId === sb.id || !(sb.image_url || sb.thumbnail_url);
    // 実在人物フィルタが指した絵コンテは赤枠 + 注意ラベルで示す (URL のクエリ除去照合)
    const facePrivacyFlaggedCard = facePrivacyStoryboardUrls.size > 0 && (
      facePrivacyStoryboardUrls.has(stripUrlQuery(sb.image_url))
      || facePrivacyStoryboardUrls.has(stripUrlQuery(sb.thumbnail_url))
    );
    return (
      <div
        key={sb.id}
        className={`${styles.storyboardCard} ${sb.is_selected ? styles.storyboardCardSelected : ''} ${facePrivacyFlaggedCard ? styles.facePrivacyFlaggedControl : ''}`}
      >
        <button
          type="button"
          onClick={() => {
            selectMutation.mutate({ scenarioId: scenario.id, storyboardId: sb.id });
            // 右パネルがストーリーボード表示中なら、クリックした候補の画像へ差し替える
            // (閉じている / 素材一覧表示中はパネルに触れない)
            if (isStoryboardPanelOpen) {
              const previewUrl = sb.image_url || sb.thumbnail_url;
              if (previewUrl) {
                openStoryboardPreview(previewUrl, letter, scenario.title);
                panelSyncedScenarioIdRef.current = scenario.id;
              }
            }
          }}
          disabled={selectMutation.isPending}
          className={styles.storyboardCardThumb}
          title={sb.is_selected ? '選択中' : '動画化するストーリーボードに選択'}
        >
          {url ? (
            <img src={url} alt="ストーリーボード候補" />
          ) : (
            <span className={styles.storyboardCardThumbEmpty}>画像なし</span>
          )}
        </button>
        {/* レター + 選択チェックを一体化したコーナーラベル。
            素材パネルの「選択中」ラベル同様、左上角に密着させ枠と一体化させる。
            選択中はブランド色 + チェックを併記する。 */}
        <span
          className={`${styles.storyboardLetter} ${sb.is_selected ? styles.storyboardLetterSelected : ''}`}
          aria-label={sb.is_selected ? `${letter}（選択中）` : letter}
        >
          {letter}
          {sb.is_selected && (
            <span className={styles.storyboardLetterCheck}>選択中</span>
          )}
        </span>
        {/* 顔を描かないモードで生成された絵コンテは、登場人物の顔がマネキン化されている。
            動画生成時には自然な顔が描かれることをカード上部に明示する。 */}
        {sb.generation_metadata?.avoid_faces && (
          <span className={styles.storyboardFaceNotice}>
            顔は動画を生成する際に描かれます
          </span>
        )}
        {/* イラスト絵コンテで生成された場合、完成動画は実写になる旨を明示する。 */}
        {sb.generation_metadata?.illustration_storyboard && (
          <span className={styles.storyboardFaceNotice}>
            動画は実写で生成されます
          </span>
        )}
        {/* 実在人物フィルタが指した絵コンテ。選択し直し・削除・覆面化再生成を促す */}
        {facePrivacyFlaggedCard && (
          <span className={styles.storyboardFaceNotice}>
            実在の人物を含むと判定されました
          </span>
        )}
        {/* 操作ボタン群: カード hover で表示。素材パネルと同じ HoverPillButton を使い、
            ツールチップではなくボタン自体が横に展開してラベルを出す。 */}
        <div className={styles.storyboardOverlayActions}>
          {/* 削除: 右上 (右寄せなので左方向に展開しカード内に収まる) */}
          <div style={{ position: 'absolute', top: '6px', right: '6px' }}>
            <HoverPillButton
              variant="danger"
              label="削除"
              icon={<img src="/bin_1_white.svg" alt="" />}
              onClick={() => deleteMutation.mutate({ scenarioId: scenario.id, storyboardId: sb.id })}
              disabled={deleteMutation.isPending}
            />
          </div>
          {/* ダウンロード: 左下 */}
          <div style={{ position: 'absolute', bottom: '6px', left: '6px' }}>
            <HoverPillButton
              label="ダウンロード"
              icon={<img src="/download_bottom.svg" alt="" />}
              onClick={() => handleDownload(sb, letter)}
              disabled={downloadDisabled}
            />
          </div>
          {/* 拡大: 右下 (右寄せなので左方向に展開) */}
          <div style={{ position: 'absolute', bottom: '6px', right: '6px' }}>
            <HoverPillButton
              label="拡大表示"
              icon={<img src="/layout-sidebar-right-expand.svg" alt="" />}
              onClick={() => handleExpand(sb, letter)}
              disabled={expandDisabled}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 2 カラム: 左 = ストーリーボードのカード + 候補列、右 = シーン一覧 + 動画/BGM */}
      <div ref={layoutRef} className={`${styles.storyboardLayout} ${isNarrowLayout ? styles.storyboardLayoutStacked : ''}`} style={{ marginBottom: '20px' }}>
        <div className={`${styles.storyboardColumn} ${isColumnCollapsed ? styles.storyboardColumnCollapsed : ''} ${isColumnCollapsed && storyboards.length === 0 && !isGenerating ? styles.storyboardColumnCollapsedHint : ''}`}>
          {isColumnCollapsed ? (
            <div className={styles.storyboardCollapsedBar}>
              {storyboards.length === 0 && !isGenerating ? (
                // ストーリーボード画像が一件も無いときは、畳んだ状態でも生成導線が
                // ひと目で分かるよう「開く」ボタンの右にテキストラベルを添える。
                <>
                  <button
                    type="button"
                    onClick={() => setIsColumnCollapsed(false)}
                    aria-label="ストーリーボードを生成"
                    className={`${styles.storyboardColumnExpand} ${styles.storyboardColumnExpandHighlight}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 6 15 12 9 18" />
                    </svg>
                  </button>
                  <span
                    className={styles.storyboardCollapsedGenerateLabel}
                    onClick={() => setIsColumnCollapsed(false)}
                  >
                    ストーリーボードを生成
                  </span>
                </>
              ) : (
                <Tooltip content="ストーリーボード生成パネルを展開">
                  <button
                    type="button"
                    onClick={() => setIsColumnCollapsed(false)}
                    aria-label="ストーリーボード生成パネルを展開"
                    className={styles.storyboardColumnExpand}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 6 15 12 9 18" />
                    </svg>
                  </button>
                </Tooltip>
              )}
              {/* 折り畳み時の文字バッジ: A, B, C, ... をクリックで選択切替 */}
              {storyboards.map((sb, i) => {
                const letterIdx = typeof sb.letter_index === 'number' ? sb.letter_index : i;
                const letter = String.fromCharCode(65 + letterIdx);
                const isActive = sb.is_selected;
                return (
                  <button
                    key={sb.id}
                    type="button"
                    onClick={() => handleLetterClick(sb, letter)}
                    disabled={selectMutation.isPending}
                    title={isActive ? `${letter}（選択中）` : `${letter} を選択`}
                    aria-label={isActive ? `${letter}（選択中）` : `${letter} を選択`}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: 700,
                      lineHeight: 1,
                      transition: 'background 0.15s ease, color 0.15s ease',
                      background: isActive
                        ? 'rgb(var(--brand-primary-rgb))'
                        : 'rgba(var(--brand-primary-rgb) / 0.08)',
                      border: isActive
                        ? '1px solid rgb(var(--brand-primary-rgb))'
                        : '1px solid rgba(var(--brand-primary-rgb) / 0.25)',
                      color: isActive ? '#1a1408' : 'rgba(245, 241, 230, 0.9)',
                    }}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          ) : (
            <>
          {/* 設定パネル: glass 風コンテナに枚数 chips / 詳細設定 / 生成ボタンを集約 */}
          <div className={styles.storyboardSettings}>
            <div className={styles.storyboardSettingsHeader}>
              <Tooltip content="ストーリーボード設定を畳む">
                <button
                  type="button"
                  onClick={() => setIsColumnCollapsed(true)}
                  aria-label="ストーリーボード設定を畳む"
                  className={styles.storyboardColumnCollapse}
                  style={{ color: 'rgba(245, 241, 230, 0.7)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 6 9 12 15 18" />
                  </svg>
                </button>
              </Tooltip>
              <span className={styles.storyboardSettingsTitle}>ストーリーボード</span>
            </div>

            {/* 詳細設定 (生成枚数・フレーム・解像度・ロゴ利用・モデル表記) */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className={styles.storyboardAdvancedToggle}
                aria-expanded={showAdvanced}
              >
                <span className={`${styles.storyboardAdvancedToggleArrow} ${showAdvanced ? styles.storyboardAdvancedToggleArrowOpen : ''}`}>▸</span>
                詳細設定
              </button>
              {showAdvanced && (
                <div className={styles.storyboardAdvancedPanel}>
                  {/* 生成枚数と解像度は左右に並べて1行で表示する */}
                  <div className={styles.storyboardAdvancedFieldRow}>
                    <div className={styles.storyboardAdvancedField}>
                      <span className={styles.storyboardFieldLabel}>生成枚数</span>
                      <select
                        className={styles.storyboardSelect}
                        value={count}
                        onChange={(e) => setCount(Number(e.target.value))}
                        disabled={isGenerating}
                        aria-label="生成枚数"
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>{n}枚</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.storyboardAdvancedField}>
                      <span className={styles.storyboardFieldLabel}>
                        解像度
                        <HelpTooltip ariaLabel="解像度設定のヘルプ">
                          {textDrivenStoryboard ? (
                            <>
                              選択中の動画モデルではストーリーボードは動画生成に使われず
                              確認用のため、高速・低ポイントのモデルで 1K 固定で生成します。
                            </>
                          ) : (
                            <>
                              絵コンテは高速・低ポイントのモデルで 1K 固定で生成します。
                            </>
                          )}
                        </HelpTooltip>
                      </span>
                      <select
                        className={styles.storyboardSelect}
                        value="1K"
                        disabled
                        aria-label="解像度"
                      >
                        <option value="1K">1K（自動）</option>
                      </select>
                    </div>
                  </div>
                  {/* フレーム: テキスト駆動モデル (絵コンテが目視確認専用) では
                      最初のフレームだけで十分なため、選択肢を出さず裏側で
                      'first_only' 固定にする (handleGenerate 側で強制) */}
                  {!textDrivenStoryboard && (
                  <div className={styles.storyboardAdvancedField}>
                    <span className={styles.storyboardFieldLabel}>
                      フレーム
                      <HelpTooltip ariaLabel="フレーム設定のヘルプ">
                        各コマに描かせる動画フレームを指定します。
                        <br />・最初: スタートフレーム 1 枚のみを描かせる（既定）
                        <br />・最初+最後: スタートとエンドの 2 枚を並べて描かせる
                      </HelpTooltip>
                    </span>
                    <select
                      className={styles.storyboardSelect}
                      value={frameMode}
                      onChange={(e) => setFrameMode(e.target.value as StoryboardFrameMode)}
                      disabled={isGenerating}
                      aria-label="フレーム"
                    >
                      {STORYBOARD_FRAME_MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  )}
                  {/* ロゴを利用: 商品にブランドロゴが紐づくときだけアクティブ。
                      studio 静止画の include_logo と同等の挙動。チェック状態は
                      localStorage に永続化 (既定 ON)。 */}
                  <div className={styles.storyboardAdvancedField}>
                    <div className={styles.storyboardLogoToggleRow}>
                      {brandLogosAvailable ? (
                        <label className={`${styles.storyboardLogoToggle} ${facePrivacyLogoFlagged ? styles.facePrivacyFlaggedControl : ''}`}>
                          <input
                            type="checkbox"
                            checked={includeLogo}
                            onChange={(e) => setIncludeLogo(e.target.checked)}
                            disabled={isGenerating}
                          />
                          <span>ロゴ画像を添付</span>
                        </label>
                      ) : (
                        <Tooltip content="ロゴ画像を添付するには、商品にブランドロゴを登録してください">
                          <label className={`${styles.storyboardLogoToggle} ${styles.storyboardLogoToggleDisabled}`}>
                            <input type="checkbox" checked={false} readOnly />
                            <span>ロゴ画像を添付</span>
                          </label>
                        </Tooltip>
                      )}
                      {individualRefsMode ? (
                        <InlineAttachPreviewButton
                          images={individualLogoUrls}
                          title="添付されるロゴ画像"
                          dimmedUrls={dimmedLogoUrls}
                          dimmedNote="グレー表示のロゴは300px未満のため添付されません"
                        />
                      ) : logoSheetBrand && (
                        <InlineDownloadButton
                          onClick={handleDownloadLogoSheet}
                          busy={downloadingLogoSheet}
                          title="ロゴを1枚のシート画像でダウンロード"
                        />
                      )}
                    </div>
                  </div>
                  {/* {商品}画像を添付する: 動画生成時に商品メイン画像を参照に足す
                      (静止画には効かない)。シナリオに商品が紐づくときだけアクティブ。
                      既定 OFF、localStorage に永続化。下に暫定の素材グリッドDLアイコンを置く。
                      個別添付モデル (Seedance 2.5 等) はチェックに依存せず選択素材を常に
                      個別添付する (useVideoHandlers) ため、この行自体を出さない。 */}
                  {!individualRefsMode && (
                  <div className={styles.storyboardAdvancedField}>
                    <div className={styles.storyboardLogoToggleRow}>
                      {hasScenarioProduct ? (
                        <label className={`${styles.storyboardLogoToggle} ${facePrivacyProductFlagged ? styles.facePrivacyFlaggedControl : ''}`}>
                          <input
                            type="checkbox"
                            checked={includeProductImage}
                            onChange={(e) => setIncludeProductImage(e.target.checked)}
                            disabled={isGenerating}
                          />
                          <span>{brandConfig.labels.productShort}画像を添付</span>
                        </label>
                      ) : (
                        <Tooltip content="商品が紐づいているシナリオでのみ利用できます">
                          <label className={`${styles.storyboardLogoToggle} ${styles.storyboardLogoToggleDisabled}`}>
                            <input type="checkbox" checked={false} readOnly />
                            <span>{brandConfig.labels.productShort}画像を添付</span>
                          </label>
                        </Tooltip>
                      )}
                      {selectedAssets.length > 0 && handleDownloadAssetTile && (
                        <InlineDownloadButton
                          onClick={handleDownloadAssetTile}
                          busy={isDownloadingAssetTile}
                          title="素材を1枚の画像でダウンロード"
                        />
                      )}
                      {/* 商品画像を再生成: 現在の選択素材から統合タイル画像を合成し直し、
                          以後の動画生成の商品画像添付に使う。顔入り商品画像が Seedance に
                          弾かれた際、顔なしの素材選択でやり直すための回避手段 */}
                      {selectedAssets.length > 0 && handleRegenerateProductTile && (
                        <Tooltip content={`${brandConfig.labels.productShort}画像を再生成`}>
                          <button
                            type="button"
                            className={styles.storyboardInlineDownloadBtn}
                            onClick={handleRegenerateProductTile}
                            disabled={isRegeneratingProductTile}
                            aria-label={`${brandConfig.labels.productShort}画像を再生成`}
                          >
                            {isRegeneratingProductTile ? (
                              <span className={styles.spinnerSmall} style={{ width: '13px', height: '13px' }} />
                            ) : (
                              <img src="/refresh.svg" alt="" className={styles.storyboardInlineDownloadIcon} />
                            )}
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  )}
                  {/* 顔画像を含めない: シナリオ設定の「顔を描かない」と同一設定 (双方向同期)。
                      Seedance 等で顔出し不可のとき、設定し忘れにここで気づいて直せるようにする。
                      以降に生成する絵コンテ・フレーム・動画のプロンプトに反映される。
                      TODO: 顔画像を含めないモードだとサングラス指定が効かないため一旦非表示。
                      原因が解消したらこのトグルを復活させる。state/同期ロジックは残置。 */}
                  {false && (
                  <div className={styles.storyboardAdvancedField}>
                    <div className={styles.storyboardLogoToggleRow}>
                      <label className={styles.storyboardLogoToggle}>
                        <input
                          type="checkbox"
                          checked={avoidFaces}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setAvoidFaces(v);
                            // 「イラストで描く」とは排他
                            if (v) setIllustrationStoryboard(false);
                          }}
                          disabled={isGenerating}
                        />
                        <span>顔画像を含めない</span>
                      </label>
                    </div>
                  </div>
                  )}
                  {/* 絵コンテをイラスト(手書き風)で描く: 廃止済みトグル。絵コンテは
                      Seedance 2.5 ガイド推奨の線画フォーマットへ全面移行し (2026-08-14)、
                      backend がフラグに依らず常に線画 (stylized 系は当該スタイル) で
                      生成するため非表示化。state/同期ロジックは互換のため残置。 */}
                  {false && (
                  <div className={styles.storyboardAdvancedField}>
                    <div className={styles.storyboardLogoToggleRow}>
                      <label className={styles.storyboardLogoToggle} style={isStylized ? { opacity: 0.4 } : undefined}>
                        <input
                          type="checkbox"
                          checked={illustrationStoryboard && !isStylized}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setIllustrationStoryboard(v);
                            // 「顔画像を含めない」とは排他
                            if (v) setAvoidFaces(false);
                          }}
                          disabled={isGenerating || isStylized}
                        />
                        <span>絵コンテをイラストで描く</span>
                      </label>
                    </div>
                  </div>
                  )}
                  <span className={styles.storyboardModelTag} title="使用する画像生成モデル">
                    <img src="/lock.svg" alt="" style={{ width: '10px', height: '10px' }} />
                    Gemini 標準
                  </span>
                </div>
              )}
            </div>

            {/* 生成ボタン */}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || preparingLogoSheet}
              className={`${styles.storyboardGenerateButton} ${isGenerating || preparingLogoSheet ? 'ai-loading-active' : ''}`}
            >
              {isGenerating || preparingLogoSheet ? (
                <>
                  {preparingLogoSheet ? 'ロゴ準備中' : '生成中'}
                  <span className="ai-loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </>
              ) : (
                <>
                  {storyboards.length > 0 ? 'ストーリーボードを追加生成' : 'ストーリーボードを生成'}
                  {storyboardCredits != null && (
                    <span className={styles.storyboardGenerateBadge}>{storyboardCredits}</span>
                  )}
                </>
              )}
            </button>
            {generateError && (
              <p className={styles.storyboardGenerateError}>
                {generateError}
              </p>
            )}
          </div>

          {/* 絵コンテ候補が出揃って未選択のときの案内: 一覧 (A) の直上に出す。
              ブランド色のアクションバナー (subtle なパルス演出付き) */}
          {storyboards.length > 0 && !hasSelection && !isGenerating && (
            <div className={styles.storyboardSelectBanner}>
              <span className={styles.storyboardSelectBannerDot} aria-hidden />
              ストーリーボードを1つ選択してください
            </div>
          )}

          {/* 絵コンテ候補・プレースホルダ（未生成でも枚数ぶん並べる） */}
          {(() => {
            // 未生成かつ非生成中のときは「ストーリーボード1」スロットを生成促し付き
            // アップロードタイルに置き換え、末尾の通常アップロードタイルは隠す。
            const isEmptyStart = storyboards.length === 0 && !isGenerating;

            const uploadTileClass = [
              styles.storyboardUploadTile,
              isDragOver ? styles.storyboardUploadTileActive : '',
              uploadMutation.isPending ? styles.storyboardUploadTileBusy : '',
            ].filter(Boolean).join(' ');

            const uploadTileEventHandlers = {
              onClick: () => {
                if (uploadMutation.isPending) return;
                openMaterialsPanel('manage');
              },
              onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (uploadMutation.isPending) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openMaterialsPanel('manage');
                }
              },
              onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
                if (uploadMutation.isPending) return;
                e.preventDefault();
                if (!isDragOver) setIsDragOver(true);
              },
              onDragLeave: () => setIsDragOver(false),
              onDrop: (e: React.DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                setIsDragOver(false);
                if (uploadMutation.isPending) return;

                // 1) 素材パネルからの素材データ (buildDragData)
                const jsonData = e.dataTransfer?.getData('application/json');
                if (jsonData) {
                  try {
                    const materialData = JSON.parse(jsonData);
                    handleUploadMaterial(materialData);
                    return;
                  } catch {
                    // 解析失敗時はブラウザファイル drop にフォールバック
                  }
                }

                // 2) ブラウザからの直接ファイル D&D
                const file = e.dataTransfer?.files?.[0];
                if (file) handleUploadFile(file);
              },
            };

            const fileSelectButton = (
              <button
                type="button"
                className={styles.storyboardUploadTileButton}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                ファイルを選択
              </button>
            );

            const fileInput = (
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadFile(file);
                  // 同じファイル再選択を許すため value をクリア
                  e.target.value = '';
                }}
              />
            );

            // 「ストーリーボードを生成してください」ヘッダー付き版。
            // 未生成スロット1で使う。
            const renderUploadTileWithHeader = (key: string) => (
              <div key={key} className={uploadTileClass} role="button" tabIndex={0} {...uploadTileEventHandlers}>
                {uploadMutation.isPending ? (
                  <span className={styles.storyboardPlaceholderGenerating}>
                    <span className={styles.spinnerSmall} style={{ marginRight: 0 }} />
                    アップロード中
                  </span>
                ) : (
                  <>
                    <span style={{ textAlign: 'center', fontWeight: 600, color: 'rgba(245, 241, 230, 0.85)' }}>
                      ストーリーボードを<br />生成してください
                    </span>
                    <div className={styles.placeholderDivider}><span>または</span></div>
                    <span style={{ textAlign: 'center' }}>
                      素材パネルから<br />ドラッグ＆ドロップ
                    </span>
                    {fileSelectButton}
                  </>
                )}
                {fileInput}
              </div>
            );

            // 末尾アップロードタイル: ヘッダー無し。シナリオに 1 枚以上ストーリーボードが
            // 存在するか、生成中の間に表示する。
            const renderTrailingUploadTile = () => (
              <div className={uploadTileClass} role="button" tabIndex={0} {...uploadTileEventHandlers}>
                {uploadMutation.isPending ? (
                  <span className={styles.storyboardPlaceholderGenerating}>
                    <span className={styles.spinnerSmall} style={{ marginRight: 0 }} />
                    アップロード中
                  </span>
                ) : (
                  <>
                    <span style={{ textAlign: 'center' }}>
                      素材パネルから<br />ドラッグ＆ドロップ
                    </span>
                    <div className={styles.placeholderDivider}><span>または</span></div>
                    {fileSelectButton}
                  </>
                )}
                {fileInput}
              </div>
            );

            return (
              <div className={styles.storyboardCards}>
                {Array.from({ length: slotCount }).map((_, i) => {
                  // 生成中も既存候補はそのまま並べ、storyboards.length 以降のスロットだけ
                  // 「生成中」のプレースホルダにする。
                  const sb = storyboards[i];
                  if (sb) {
                    // 画像とアルファベットを 1:1 に対応させる。letter_index が未採番の旧データは
                    // 表示順にフォールバック（マイグレーション後はすべての行に値がある）。
                    const letterIdx = typeof sb.letter_index === 'number' ? sb.letter_index : i;
                    return renderStoryboardCard(sb, String.fromCharCode(65 + letterIdx));
                  }

                  // 未生成時のスロット 1: ヘッダー付きアップロードタイル
                  if (i === 0 && isEmptyStart) {
                    return renderUploadTileWithHeader(`storyboard-placeholder-${i}`);
                  }

                  // 未生成時のスロット 2 以降は非表示 (UI を空きでうるさくしない)。
                  if (isEmptyStart) {
                    return null;
                  }

                  return (
                    <div key={`storyboard-placeholder-${i}`} className={styles.storyboardPlaceholder}>
                      {isGenerating ? (
                        <span className={styles.storyboardPlaceholderGenerating}>
                          <span className={styles.spinnerSmall} style={{ marginRight: 0 }} />
                          生成中
                        </span>
                      ) : (
                        <span className={styles.storyboardPlaceholderNumber}>{i + 1}</span>
                      )}
                    </div>
                  );
                })}
                {/* 末尾アップロードタイル: 未生成 (= スロット1 に置き換え済み) 時は非表示 */}
                {!isEmptyStart && renderTrailingUploadTile()}
              </div>
            );
          })()}
            </>
          )}
        </div>

        {/* 右列: シーン一覧 + 動画カードと前後のセクション。左列が畳まれている (= メイン領域が狭い)
            間はシーン見出しを簡略化するため、その状態を Context で配下に伝える。 */}
        <div className={styles.storyboardSceneColumn}>
          <StoryboardCompactContext.Provider value={isColumnCollapsed}>
            {children}
          </StoryboardCompactContext.Provider>
          {beforeVideoSection}
          {/* 動画カード: シーン一覧と beforeVideoSection の下に常時表示。絵コンテ未選択のあいだは
              「動画を生成」ボタンを非活性化し、必要な操作をツールチップで案内する */}
          {videoBox && (
            <div style={{ marginTop: '16px' }}>
              {videoBox(
                hasSelection
                  ? null
                  : storyboards.length === 0
                    ? 'まずはストーリーボードを生成・選択してください'
                    : '動画化するストーリーボードを選択してください',
              )}
            </div>
          )}
          {afterVideoSection && <div style={{ marginTop: '16px' }}>{afterVideoSection}</div>}
        </div>
      </div>

    </>
  );
}
